import {
  ApolloClient,
  ApolloProvider as BaseApolloProvider,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { createUploadLink } from 'apollo-upload-client';
import { useCurrentUserContext } from 'components/contexts/CurrentUserContext';
import { API_URI, isDev } from 'consts/config';
import { LOCAL_STORAGE_KEYS } from 'consts/ls-keys';
import { useAuth0 } from 'hooks/auth/useAuth0';
import { useSnackbar } from 'notistack';
import React, { useMemo, useRef } from 'react';
import { ANONYMOUS_LOCATIONS } from 'routes';

import cache from './cache';

const SERVER_ERROR_MESSAGE = 'Internal server error';
// const ABN_ERROR_TEXT =
//   'Please contact ABN AMRO or OpenTalent support to get access to the application.';

const useHttpLink = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { value: currentUser } = useCurrentUserContext();

  const getAccessTokenRef = useRef(getAccessTokenSilently);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const currentUserRef = useRef(currentUser);

  getAccessTokenRef.current = getAccessTokenSilently;
  isAuthenticatedRef.current = isAuthenticated;
  currentUserRef.current = currentUser;

  return useMemo(() => {
    const authLink = setContext(async (_, { headers }) => {
      let token = '';
      const needToken =
        !ANONYMOUS_LOCATIONS.some((location) =>
          window.location.pathname.includes(location),
        ) || isAuthenticatedRef.current;

      try {
        if (needToken) {
          token = await getAccessTokenRef.current();
        }
      } catch (e) {
        console.error(e);
      }

      return {
        headers: {
          ...headers,
          ...(token && { authorization: `Bearer ${token}` }),
          ...(currentUserRef.current?.type && {
            'Ot-Api-Consumer-Type': currentUserRef.current?.type,
          }),
          ...(currentUserRef.current?.userId && {
            'Ot-Api-Consumer-Id': currentUserRef.current?.userId,
          }),
        },
      };
    });

    return authLink.concat(createUploadLink({ uri: API_URI }));
  }, []);
};

const useErrorLink = () => {
  const { enqueueSnackbar } = useSnackbar();
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;

  return useMemo(
    () =>
      onError(({ graphQLErrors, networkError, operation }) => {
        if (graphQLErrors) {
          graphQLErrors.forEach((error) => {
            const { message, locations, path } = error;
            enqueueSnackbarRef.current(message, { variant: 'error' });
            console.error(
              `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
                locations,
              )}, Path: ${path}`,
            );

            if (message === SERVER_ERROR_MESSAGE) {
              Sentry.captureException(new Error(`[API error]: ${message}`), {
                level: 'error',
                extra: {
                  operation: JSON.stringify(operation, null, 1),
                  originalError: error,
                  trace: (error as any).trace,
                },
              });
            }
          });
        }

        if (networkError) {
          console.error(`[Network error]: ${networkError}`);
          const statusCode = (networkError as any).statusCode;
          const message =
            (networkError as any)?.result?.message || networkError?.message;

          // Only store auth errors for permanent failures, not temporary network issues
          if (
            statusCode === 401 ||
            statusCode === 403 ||
            message?.includes('Unauthorized') ||
            message?.includes('Forbidden')
          ) {
            localStorage.setItem(LOCAL_STORAGE_KEYS.authError, message);
          }

          enqueueSnackbarRef.current(message, { variant: 'error' });
        }
      }),
    [],
  );
};

const useApolloClient = () => {
  const httpLink = useHttpLink();
  const errorLink = useErrorLink();

  return useMemo(
    () =>
      new ApolloClient({
        link: from([errorLink, httpLink]),
        cache,
        devtools: { enabled: isDev },
      }),
    [errorLink, httpLink],
  );
};

const ApolloProvider = ({
  children,
}: React.PropsWithChildren<Record<string, unknown>>) => {
  const client = useApolloClient();
  return <BaseApolloProvider client={client}>{children}</BaseApolloProvider>;
};

export default ApolloProvider;
